import { Box } from "@chakra-ui/react";
import { useCallback } from "react";
import { SegmentedControl } from "design-system/components/SegmentedControl";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import NewHireJourneySelectByIds from "features/NewHireJourney/Components/NewHireJourneySelectByIds";
import { NewHireJourneyId } from "models/newHire";

export type MyTasksStatusFilter = "not_started" | "open" | "closed";

export const DEFAULT_STATUS_FILTER: MyTasksStatusFilter = "open";

const STATUS_FILTER_OPTIONS = [
  { value: "not_started" as const, label: "Not Started" },
  { value: "open" as const, label: "Open" },
  { value: "closed" as const, label: "Closed" },
];

export interface MyTasksFiltersState {
  q?: string;
  statusFilter?: MyTasksStatusFilter;
  newHireId?: string | null;
}

export type ViewMode = "journey" | "list";

export const JOURNEY_SORT_OPTIONS = [
  { label: "Most Open Tasks", value: "most_open" },
  { label: "New Hire Start: Newest First", value: "new_hire_start_desc" },
  { label: "New Hire Start: Oldest First", value: "new_hire_start_asc" },
];

export const LIST_SORT_OPTIONS = [
  { label: "Due Date: Soonest First", value: "due_date_asc" },
  { label: "Due Date: Latest First", value: "due_date_desc" },
  { label: "Start Date: Soonest First", value: "start_date_asc" },
  { label: "Start Date: Latest First", value: "start_date_desc" },
  { label: "Task Name: A-Z", value: "name_asc" },
  { label: "Task Name: Z-A", value: "name_desc" },
];

export const DEFAULT_JOURNEY_SORT = "most_open";
export const DEFAULT_LIST_SORT = "due_date_asc";

interface MyTasksFiltersProps {
  filters: MyTasksFiltersState;
  onFiltersChange: (filters: MyTasksFiltersState) => void;
  onReset: () => void;
  viewMode: ViewMode;
  initialNewHireJourneyIds: NewHireJourneyId[];
  currentSort: string;
  onSortChange: (value: string) => void;
}

export default function MyTasksFilters({
  filters,
  onFiltersChange,
  onReset,
  viewMode,
  initialNewHireJourneyIds,
  currentSort,
  onSortChange,
}: MyTasksFiltersProps) {
  const handleFilterChange = useCallback(
    (key: keyof MyTasksFiltersState, value: string | boolean | undefined | null) => {
      onFiltersChange({
        ...filters,
        [key]: value,
      });
    },
    [filters, onFiltersChange],
  );

  const sortOptions = viewMode === "journey" ? JOURNEY_SORT_OPTIONS : LIST_SORT_OPTIONS;
  const defaultSort = viewMode === "journey" ? DEFAULT_JOURNEY_SORT : DEFAULT_LIST_SORT;

  return (
    <FilterSortSearchBar
      searchPlaceholder="Search tasks..."
      searchValue={filters.q}
      onSearchChange={(value) => handleFilterChange("q", value || undefined)}
      currentSort={currentSort}
      defaultSort={defaultSort}
      onSortChange={onSortChange}
      sortOptions={sortOptions}
      quickFilters={
        <>
          <SegmentedControl
            options={STATUS_FILTER_OPTIONS}
            value={filters.statusFilter ?? DEFAULT_STATUS_FILTER}
            onChange={(value) => handleFilterChange("statusFilter", value)}
            size="sm"
          />
          <Box minW="220px">
            <NewHireJourneySelectByIds
              initialNewHireJourneyIds={initialNewHireJourneyIds}
              selectedNewHireJourneyId={filters.newHireId || null}
              onChange={(e) => handleFilterChange("newHireId", (e as string) || null)}
              placeholder="Filter by New Hire"
            />
          </Box>
        </>
      }
      activeFilters={[]}
      onReset={onReset}
      hasActiveFilters={false}
    />
  );
}
